import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Archive, Boxes, Loader2, PackageOpen, RefreshCw, Search, ScrollText, SendHorizontal } from "lucide-react";
import { authApi, getApiErrorMessage } from "../api";
import AddOldStockModal from "./AddOldStockModal";
import LogisticsModal from "./LogisticsModal";

const S = `
  .os-wrap { padding: 24px; font-family: Georgia, 'Times New Roman', serif; color: #172554; background:
    radial-gradient(circle at top left, rgba(254,240,138,.28), transparent 34%),
    linear-gradient(180deg, #fffdf5 0%, #f8fafc 100%); min-height: 100%; }
  .os-head { display:flex; justify-content:space-between; align-items:flex-start; gap:12px; flex-wrap:wrap; margin-bottom:18px; }
  .os-title { margin:0; font-size:28px; font-weight:800; color:#78350f; }
  .os-sub { font-size:13px; color:#92400e; margin-top:6px; max-width:780px; }
  .os-btn { border:1px solid #f59e0b; background:#fff; color:#92400e; border-radius:12px; padding:10px 14px; font-size:13px; font-weight:700; cursor:pointer; display:inline-flex; align-items:center; gap:8px; }
  .os-btn-primary { background: linear-gradient(135deg,#d97706,#b45309); color:#fff; border-color:#b45309; }
  .os-tools { display:grid; grid-template-columns: 2fr 1fr 1fr 1fr auto; gap:10px; margin-bottom:18px; }
  .os-input, .os-select { width:100%; box-sizing:border-box; border:1px solid #cbd5e1; border-radius:12px; padding:11px 12px; background:#fff; color:#1e293b; font-size:13px; }
  .os-kpis { display:grid; grid-template-columns: repeat(5, minmax(0,1fr)); gap:14px; margin-bottom:20px; }
  .os-kpi { background:#fff; border:1px solid #e2e8f0; border-radius:18px; padding:18px; box-shadow:0 10px 24px rgba(15,23,42,.05); }
  .os-kpi-label { font-size:12px; color:#64748b; font-weight:700; }
  .os-kpi-value { font-size:28px; font-weight:900; margin-top:8px; color:#0f172a; }
  .os-kpi-sub { font-size:12px; color:#94a3b8; margin-top:6px; }
  .os-grid { display:grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap:16px; }
  .os-card { background:#fff; border:1px solid #e2e8f0; border-radius:20px; padding:18px; box-shadow:0 12px 28px rgba(15,23,42,.06); display:grid; gap:12px; }
  .os-card-top { display:flex; justify-content:space-between; gap:8px; align-items:flex-start; }
  .os-code { font-size:18px; font-weight:900; color:#1e3a8a; }
  .os-badges { display:flex; gap:6px; flex-wrap:wrap; }
  .os-badge { display:inline-flex; padding:4px 10px; border-radius:999px; font-size:11px; font-weight:800; }
  .os-meta { display:grid; gap:7px; font-size:13px; color:#334155; }
  .os-meta-row { display:flex; justify-content:space-between; gap:10px; }
  .os-actions { display:flex; gap:8px; flex-wrap:wrap; margin-top:4px; }
  .os-empty { padding:56px 12px; text-align:center; color:#64748b; }
  .os-modal-overlay { position:fixed; inset:0; background:rgba(15,23,42,.5); z-index:1250; display:flex; align-items:center; justify-content:center; padding:18px; }
  .os-modal { width:min(760px,100%); background:#fff; border-radius:20px; box-shadow:0 22px 50px rgba(15,23,42,.24); overflow:hidden; }
  .os-modal-head { display:flex; justify-content:space-between; align-items:center; gap:10px; padding:18px 20px; border-bottom:1px solid #e2e8f0; }
  .os-modal-body { padding:20px; display:grid; gap:14px; max-height:76vh; overflow:auto; }
  .os-history-row { border:1px solid #e2e8f0; border-radius:14px; padding:12px; display:grid; gap:4px; }
  .os-age-green { color:#166534; }
  .os-age-amber { color:#b45309; }
  .os-age-red { color:#b91c1c; }
  .os-inline { display:grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap:12px; }
  .os-segment { display:flex; gap:8px; flex-wrap:wrap; }
  .os-segment button { border:1px solid #cbd5e1; background:#fff; border-radius:999px; padding:9px 14px; font-size:12px; font-weight:800; cursor:pointer; color:#334155; }
  .os-segment button.active { background:#0f172a; color:#fff; border-color:#0f172a; }
  .os-box-list { display:grid; gap:8px; }
  .os-box-row { display:flex; justify-content:space-between; gap:8px; padding:10px 12px; border:1px solid #e2e8f0; border-radius:12px; align-items:center; flex-wrap:wrap; }
  .os-check { display:flex; align-items:center; gap:8px; }
  .os-dropdown { border:1px solid #e2e8f0; border-radius:14px; background:#fff; max-height:180px; overflow:auto; }
  .os-option { padding:10px 12px; border-bottom:1px solid #f1f5f9; cursor:pointer; }
  .os-option:last-child { border-bottom:none; }
  .os-option:hover { background:#f8fafc; }
  @media (max-width: 980px) { .os-tools, .os-kpis, .os-inline { grid-template-columns: 1fr; } }
`;

const fmtNum = (value, fraction = 0) => Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: fraction, maximumFractionDigits: fraction });
const ageTone = (days) => (days > 90 ? "os-age-red" : days > 30 ? "os-age-amber" : "os-age-green");

function HistoryModal({ row, history, loading, onClose }) {
  if (!row) return null;

  const openPdf = async (entry, download = false) => {
    const backendRoot = (process.env.REACT_APP_API_URL || "http://127.0.0.1:5011/api/v1").replace(/\/api\/v1\/?$/, "");
    try {
      if (entry?.reference_type === "DISPATCH_NOTE" && entry?.reference_id) {
        const pdfRes = await authApi.post(`/dispatch-labels/${entry.reference_id}/generate`, {}, { responseType: "blob" });
        const blobUrl = window.URL.createObjectURL(new Blob([pdfRes.data], { type: "application/pdf" }));
        if (download) {
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = `${entry.dispatch_number || `dispatch_${entry.reference_id}`}.pdf`;
          document.body.appendChild(link);
          link.click();
          link.remove();
        } else {
          window.open(blobUrl, "_blank", "noopener,noreferrer");
        }
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
        return;
      }
      if (!entry?.label_pdf_path) return;
      const fileUrl = `${backendRoot}${entry.label_pdf_path}`;
      if (download) {
        const link = document.createElement("a");
        link.href = fileUrl;
        link.download = entry.label_pdf_path.split("/").pop() || "dispatch_label.pdf";
        document.body.appendChild(link);
        link.click();
        link.remove();
        return;
      }
      window.open(fileUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      window.alert(getApiErrorMessage(error, "Unable to open dispatch receipt."));
      return;
    }
  };

  return (
    <div className="os-modal-overlay" onClick={onClose}>
      <div className="os-modal" onClick={(e) => e.stopPropagation()}>
        <div className="os-modal-head">
          <div>
            <div style={{ fontSize: 18, fontWeight: 900 }}>{row.design_code}</div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>Transaction history for batch #{row.id}</div>
          </div>
          <button className="os-btn" onClick={onClose}>Close</button>
        </div>
        <div className="os-modal-body">
          {loading ? <div className="os-empty"><Loader2 size={18} className="animate-spin" /> Loading history...</div> : history.length === 0 ? <div className="os-empty">No transactions logged yet.</div> : history.map((entry) => (
            <div key={entry.id} className="os-history-row">
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <div style={{ display: "grid", gap: 4 }}>
                  <strong>{entry.transaction_type}</strong>
                  {entry.dispatch_number ? <span style={{ fontSize: 12, color: "#475569" }}>{entry.dispatch_number}</span> : null}
                </div>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  {entry.reference_type === "DISPATCH_NOTE" && entry.reference_id ? (
                    <>
                      <button className="os-btn" type="button" onClick={() => openPdf(entry, false)}>View</button>
                      <button className="os-btn os-btn-primary" type="button" onClick={() => openPdf(entry, true)}>Download</button>
                    </>
                  ) : entry.is_dispatch ? (
                    <>
                      <button className="os-btn" type="button" disabled title="Receipt not available for this legacy dispatch">View</button>
                      <button className="os-btn os-btn-primary" type="button" disabled title="Receipt not available for this legacy dispatch">Download</button>
                    </>
                  ) : null}
                  <span>{entry.created_at ? new Date(entry.created_at).toLocaleString("en-IN") : "-"}</span>
                </div>
              </div>
              <div>Pieces: {entry.pieces} | Weight: {fmtNum(entry.weight, 3)} g</div>
              <div>By: {entry.performed_by_name || "-"}</div>
              <div style={{ color: "#64748b" }}>{entry.remarks || "-"}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function QuickDispatchModal({ row, onClose, onProceed }) {
  const [dispatchMode, setDispatchMode] = useState(row?.stock_entry_mode === "BOXES" ? "BOXES" : "PIECES");
  const [pieces, setPieces] = useState(row?.available_pieces || 0);
  const [customerId, setCustomerId] = useState("");
  const [unitPrice, setUnitPrice] = useState("");
  const [remarks, setRemarks] = useState("");
  const [selectedBoxes, setSelectedBoxes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState("");
  const [showCustomers, setShowCustomers] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const availableBoxes = useMemo(() => (
    Array.isArray(row?.box_details)
      ? row.box_details.filter((box) => Number(box.available_quantity || 0) > 0)
      : []
  ), [row]);
  const selectedBoxSummary = useMemo(() => {
    return availableBoxes
      .filter((box) => selectedBoxes.includes(box.barcode))
      .reduce((acc, box) => ({
        pieces: acc.pieces + Number(box.available_quantity || 0),
        weight: acc.weight + Number(box.net_weight || 0),
      }), { pieces: 0, weight: 0 });
  }, [availableBoxes, selectedBoxes]);
  const filteredCustomers = useMemo(() => {
    const q = customerSearch.trim().toLowerCase();
    if (!q) return customers.slice(0, 20);
    return customers.filter((customer) => [customer.name, customer.contact_phone, customer.contact_email].filter(Boolean).join(" ").toLowerCase().includes(q)).slice(0, 20);
  }, [customers, customerSearch]);
  const selectedCustomer = useMemo(() => customers.find((customer) => Number(customer.id) === Number(customerId)) || null, [customers, customerId]);

  useEffect(() => {
    if (!row) return;
    authApi.get("/customers", { params: { per_page: 200 } })
      .then((res) => setCustomers(res.data?.data || []))
      .catch(() => setCustomers([]));
  }, [row]);

  if (!row) return null;

  const toggleBox = (barcode) => {
    setSelectedBoxes((prev) => prev.includes(barcode) ? prev.filter((item) => item !== barcode) : [...prev, barcode]);
  };

  const submit = async () => {
    setError("");
    if (!customerId) {
      setError("Please select a customer.");
      return;
    }
    if (dispatchMode === "PIECES" && (!Number(pieces) || Number(pieces) <= 0)) {
      setError("Please enter pieces to dispatch.");
      return;
    }
    if (dispatchMode === "BOXES" && selectedBoxes.length === 0) {
      setError("Please select at least one box.");
      return;
    }

    setSaving(true);
    try {
      onProceed?.({
        customer_id: Number(customerId),
        remarks,
        items: [{
          store_inventory_id: row.id,
          pieces: dispatchMode === "PIECES" ? Number(pieces) : undefined,
          unit_price: Number(unitPrice || 0),
          dispatch_mode: dispatchMode,
          selected_boxes: dispatchMode === "BOXES" ? selectedBoxes : undefined,
        }],
        row,
        totalWeightKg: dispatchMode === "BOXES"
          ? Number(selectedBoxSummary.weight || 0) / 1000
          : (Number(pieces || 0) * Number(row.avg_piece_weight || 0)) / 1000,
      });
      onClose?.();
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to prepare old stock dispatch."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="os-modal-overlay" onClick={onClose}>
      <div className="os-modal" onClick={(e) => e.stopPropagation()}>
        <div className="os-modal-head">
          <div>
            <div style={{ fontSize: 18, fontWeight: 900 }}>Quick Dispatch</div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>{row.design_code} | Available {row.available_pieces} pcs {row.available_boxes ? `| ${row.available_boxes} boxes` : ""}</div>
          </div>
          <button className="os-btn" onClick={onClose}>Close</button>
        </div>
        <div className="os-modal-body">
          {error ? <div style={{ border: "1px solid #fecaca", background: "#fff1f2", color: "#b91c1c", padding: 10, borderRadius: 12 }}>{error}</div> : null}
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Dispatch Type</div>
            <div className="os-segment">
              <button className={dispatchMode === "PIECES" ? "active" : ""} onClick={() => setDispatchMode("PIECES")}>Loose Pieces</button>
              <button className={dispatchMode === "BOXES" ? "active" : ""} onClick={() => setDispatchMode("BOXES")} disabled={availableBoxes.length === 0}>Selected Boxes</button>
            </div>
          </div>
          <div className="os-inline">
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Select Customer</div>
              <input
                className="os-input"
                value={customerSearch}
                placeholder="Search customer name / phone"
                onFocus={() => setShowCustomers(true)}
                onChange={(e) => {
                  setCustomerSearch(e.target.value);
                  setShowCustomers(true);
                }}
              />
              {showCustomers && filteredCustomers.length > 0 ? (
                <div className="os-dropdown">
                  {filteredCustomers.map((customer) => (
                    <div
                      key={customer.id}
                      className="os-option"
                      onClick={() => {
                        setCustomerId(customer.id);
                        setCustomerSearch(`${customer.name || ""}${customer.contact_phone ? ` - ${customer.contact_phone}` : ""}`);
                        setShowCustomers(false);
                      }}
                    >
                      <div style={{ fontWeight: 800 }}>{customer.name || "-"}</div>
                      <div style={{ fontSize: 12, color: "#64748b" }}>{customer.contact_phone || "-"} {customer.contact_email ? `| ${customer.contact_email}` : ""}</div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Unit Price</div>
              <input className="os-input" type="number" min={0} step="0.01" value={unitPrice} onChange={(e) => setUnitPrice(e.target.value)} />
            </div>
          </div>
          {selectedCustomer ? (
            <div style={{ border: "1px dashed #93c5fd", background: "#eff6ff", borderRadius: 12, padding: 12, fontSize: 13, color: "#1e3a8a" }}>
              Customer: <strong>{selectedCustomer.name || "-"}</strong>
              {selectedCustomer.contact_phone ? ` | ${selectedCustomer.contact_phone}` : ""}
              {selectedCustomer.contact_email ? ` | ${selectedCustomer.contact_email}` : ""}
            </div>
          ) : null}
          {dispatchMode === "PIECES" ? (
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Pieces</div>
              <input className="os-input" type="number" min={1} max={row.available_pieces} value={pieces} onChange={(e) => setPieces(e.target.value)} />
            </div>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              <div style={{ fontSize: 12, color: "#475569" }}>
                Selected boxes: {selectedBoxes.length} | Pieces: {selectedBoxSummary.pieces} | Net Weight: {fmtNum(selectedBoxSummary.weight, 3)} g
              </div>
              <div className="os-box-list">
                {availableBoxes.map((box) => (
                  <label key={box.barcode} className="os-box-row">
                    <div className="os-check">
                      <input type="checkbox" checked={selectedBoxes.includes(box.barcode)} onChange={() => toggleBox(box.barcode)} />
                      <div>
                        <div style={{ fontWeight: 800 }}>{box.barcode}</div>
                        <div style={{ fontSize: 12, color: "#64748b" }}>Color: {box.color_code}</div>
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: "#475569" }}>
                      Qty {box.available_quantity} | Net {fmtNum(box.net_weight, 3)} g
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Remarks</div>
            <input className="os-input" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button className="os-btn" onClick={onClose}>Cancel</button>
            <button className="os-btn os-btn-primary" disabled={saving} onClick={submit}>
              {saving ? <Loader2 size={14} className="animate-spin" /> : <SendHorizontal size={14} />} Next: Logistics
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function OldStockBrowser() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({ design_code: "", metal_type: "", metal_purity: "", min_pieces: "" });
  const [showAdd, setShowAdd] = useState(false);
  const [historyRow, setHistoryRow] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [dispatchRow, setDispatchRow] = useState(null);
  const [pendingDispatch, setPendingDispatch] = useState(null);
  const [toast, setToast] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== "" && value != null) params[key] = value;
      });
      const [listRes, summaryRes] = await Promise.all([
        authApi.get("/sales/store-inventory/old-stock", { params }),
        authApi.get("/sales/store-inventory/old-stock/summary"),
      ]);
      setRows(listRes.data?.data || []);
      setSummary(summaryRes.data?.data || null);
    } catch (err) {
      setRows([]);
      setError(err?.response?.data?.message || "Failed to load old stock.");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(""), 2500);
    return () => clearTimeout(t);
  }, [toast]);

  const showHistory = async (row) => {
    setHistoryRow(row);
    setHistoryLoading(true);
    try {
      const res = await authApi.get(`/sales/store-inventory/old-stock/${row.id}/transactions`);
      setHistory(res.data?.data || []);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const agingText = useMemo(() => {
    const aging = summary?.aging_breakdown || {};
    return `0-30d: ${aging["0_30_days"] || 0} | 30-90d: ${aging["30_90_days"] || 0} | 90+d: ${aging["90_plus"] || 0}`;
  }, [summary]);

  const confirmOldStockDispatch = async (logisticsPayload) => {
    if (!pendingDispatch) return;
    try {
      const createRes = await authApi.post("/dispatch-notes/create", {
        customer_id: pendingDispatch.customer_id,
        remarks: pendingDispatch.remarks || undefined,
        items: pendingDispatch.items,
      });
      const noteId = createRes?.data?.data?.id;
      const confirmRes = await authApi.put(`/dispatch-notes/${noteId}/confirm`, logisticsPayload);
      const labelUrl = confirmRes?.data?.label_pdf_url;
      if (labelUrl) {
        const backendRoot = (process.env.REACT_APP_API_URL || "http://127.0.0.1:5011/api/v1").replace(/\/api\/v1\/?$/, "");
        const pdfRes = await authApi.get(`${backendRoot}${labelUrl}`, { responseType: "blob" });
        const blobUrl = window.URL.createObjectURL(new Blob([pdfRes.data], { type: "application/pdf" }));
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = `shipping_label_${createRes?.data?.data?.dispatch_number || noteId}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
      }
      setPendingDispatch(null);
      setToast("Old stock dispatch confirmed and shipping label generated.");
      await load();
    } catch (err) {
      throw err;
    }
  };

  return (
    <>
      <style>{S}</style>
      <div className="os-wrap">
        <div className="os-head">
          <div>
            <h2 className="os-title">Old Stock Browser</h2>
            <div className="os-sub">Track old stock either as loose pieces or customer-ready boxes, then dispatch it in the same mode the customer asks for.</div>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="os-btn" onClick={load}><RefreshCw size={14} /> Refresh</button>
            <button className="os-btn os-btn-primary" onClick={() => setShowAdd(true)}><Archive size={14} /> Add Old Stock</button>
          </div>
        </div>

        {toast ? <div style={{ marginBottom: 14, padding: "10px 12px", borderRadius: 12, background: "#ecfccb", color: "#3f6212", border: "1px solid #bef264" }}>{toast}</div> : null}

        <div className="os-kpis">
          <div className="os-kpi"><div className="os-kpi-label">Total Old Stock Pieces</div><div className="os-kpi-value">{fmtNum(summary?.total_old_stock_pieces)}</div><div className="os-kpi-sub">{agingText}</div></div>
          <div className="os-kpi"><div className="os-kpi-label">Total Old Stock Boxes</div><div className="os-kpi-value">{fmtNum(summary?.total_old_stock_boxes)}</div><div className="os-kpi-sub">customer-packed stock ready</div></div>
          <div className="os-kpi"><div className="os-kpi-label">Total Weight</div><div className="os-kpi-value">{fmtNum(summary?.total_weight, 3)}</div><div className="os-kpi-sub">grams across aged batches</div></div>
          <div className="os-kpi"><div className="os-kpi-label">Total Value</div><div className="os-kpi-value">{fmtNum(summary?.total_value, 2)}</div><div className="os-kpi-sub">inventory valuation estimate</div></div>
          <div className="os-kpi"><div className="os-kpi-label">Metal Mix</div><div className="os-kpi-value">{(summary?.by_metal_type || []).length}</div><div className="os-kpi-sub">{(summary?.by_metal_type || []).map((row) => `${row.metal_type}: ${row.pieces}`).join(" | ") || "No stock yet"}</div></div>
        </div>

        <div className="os-tools">
          <input className="os-input" placeholder="Search design code" value={filters.design_code} onChange={(e) => setFilters((prev) => ({ ...prev, design_code: e.target.value }))} />
          <select className="os-select" value={filters.metal_type} onChange={(e) => setFilters((prev) => ({ ...prev, metal_type: e.target.value }))}>
            <option value="">All Metals</option>
            <option value="GOLD">Gold</option>
            <option value="SILVER">Silver</option>
            <option value="PLATINUM">Platinum</option>
          </select>
          <input className="os-input" placeholder="Purity" value={filters.metal_purity} onChange={(e) => setFilters((prev) => ({ ...prev, metal_purity: e.target.value }))} />
          <input className="os-input" type="number" min={0} placeholder="Min pieces" value={filters.min_pieces} onChange={(e) => setFilters((prev) => ({ ...prev, min_pieces: e.target.value }))} />
          <button className="os-btn" onClick={load}><Search size={14} /> Search</button>
        </div>

        {loading ? <div className="os-empty"><Loader2 size={18} className="animate-spin" /> Loading old stock...</div> : error ? <div className="os-empty" style={{ color: "#b91c1c" }}>{error}</div> : rows.length === 0 ? <div className="os-empty">No old stock matches these filters.</div> : (
          <div className="os-grid">
            {rows.map((row) => (
              <div key={row.id} className="os-card">
                <div className="os-card-top">
                  <div>
                    <div className="os-code">{row.design_code}</div>
                    <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>{row.design_name || row.job_card_no || "Legacy batch"}</div>
                  </div>
                  <div className="os-badges">
                    <span className="os-badge" style={{ background: "#fef3c7", color: "#92400e" }}>Old Stock</span>
                    <span className="os-badge" style={{ background: "#e0f2fe", color: "#075985" }}>{row.stock_entry_mode === "BOXES" ? "Boxes Wise" : "Pieces Wise"}</span>
                    <span className={`os-badge ${ageTone(row.days_since_created)}`} style={{ background: "#f8fafc" }}>{row.days_since_created}d</span>
                  </div>
                </div>
                <div className="os-meta">
                  <div className="os-meta-row"><span>Metal</span><strong>{row.metal_type || "-"} / {row.metal_purity || "-"}</strong></div>
                  <div className="os-meta-row"><span>Available Pieces</span><strong>{fmtNum(row.available_pieces)}</strong></div>
                  <div className="os-meta-row"><span>Available Boxes</span><strong>{fmtNum(row.available_boxes)}</strong></div>
                  <div className="os-meta-row"><span>Avg Weight</span><strong>{fmtNum(row.avg_piece_weight, 3)} g</strong></div>
                  <div className="os-meta-row"><span>Unit Cost</span><strong>{fmtNum(row.unit_cost, 2)}</strong></div>
                  <div className="os-meta-row"><span>Customer / Source</span><strong>{row.customer_name || row.job_card_no || "Manual Entry"}</strong></div>
                </div>
                <div className="os-actions">
                  <button className="os-btn os-btn-primary" onClick={() => setDispatchRow(row)}><PackageOpen size={14} /> Quick Dispatch</button>
                  <button className="os-btn" onClick={() => showHistory(row)}><ScrollText size={14} /> View History</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AddOldStockModal open={showAdd} onClose={() => setShowAdd(false)} onSuccess={() => { setToast("Old stock added."); load(); }} />
      <HistoryModal row={historyRow} history={history} loading={historyLoading} onClose={() => setHistoryRow(null)} />
      <QuickDispatchModal row={dispatchRow} onClose={() => setDispatchRow(null)} onProceed={(payload) => setPendingDispatch(payload)} />
      <LogisticsModal
        open={Boolean(pendingDispatch)}
        onClose={() => setPendingDispatch(null)}
        onConfirm={confirmOldStockDispatch}
        totalWeightKg={pendingDispatch?.totalWeightKg || 0}
        row={pendingDispatch?.row || null}
      />
    </>
  );
}
